import {showNotification} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {OrderResponseBody} from '@dms/api/finance';
import {
  BusinessCaseResponseBody,
  useCreateBrokerageInternalInvoiceDocumentDocumentMutation,
  useCreateBrokerageInternalInvoiceDocumentWithVatDocumentMutation,
  useCreateInternalCorrectiveTaxDocumentDocumentMutation,
  useCreateInternalInvoiceDocumentDocumentMutation,
  useCreateInternalInvoiceDocumentWithVatDocumentMutation,
} from '@dms/api/sales';
import {GetParticipationApiResponse} from '@dms/api/shared';
import {
  handleApiError,
  hasAclDocsWithPermission,
  useDocumentTemplates,
  usePermissions,
} from '@dms/shared';

export const useGenerateInternalInvoice = (
  order: OrderResponseBody,
  paymentId: string,
  checkoutId: string,
  businessCase: BusinessCaseResponseBody,
  taxDocumentId?: string,
  businessCaseParticipation?: GetParticipationApiResponse
) => {
  const isBrokerageInternalDocument =
    order.orderDiscriminator === 'PURCHASE_BROKERAGE_FEES' ||
    order.orderDiscriminator === 'PURCHASE_BROKERAGE_SALE';

  const [getTemplatesByCode, {isLoading: areTemplatesLoading}] = useDocumentTemplates();

  const [
    canReadBrokerageInternalInvoiceDocument,
    canReadBrokerageInternalInvoiceDocumentWithVat,
    canReadInternalInvoiceDocument,
    canReadInternalInvoiceDocumentWithVat,
    canReadInternalCorrectiveTaxDocument,
  ] = usePermissions({
    permissionKeys: [
      'readBrokerageInternalInvoiceDocument',
      'readBrokerageInternalInvoiceDocumentWithVat',
      'readInternalInvoiceDocument',
      'readInternalInvoiceDocumentWithVat',
      'readInternalCorrectiveTaxDocument',
    ],
    scopes: {
      readBrokerageInternalInvoiceDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase.branchId,
      },
      readBrokerageInternalInvoiceDocumentWithVat: {
        participation: businessCaseParticipation,
        branchId: businessCase.branchId,
      },
      readInternalInvoiceDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase.branchId,
      },
      readInternalInvoiceDocumentWithVat: {
        participation: businessCaseParticipation,
        branchId: businessCase.branchId,
      },
      readInternalCorrectiveTaxDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase.branchId,
      },
    },
  });

  const [createInternalDocument, {isLoading: isCreateInternalDocumentLoading}] =
    useCreateInternalInvoiceDocumentDocumentMutation();
  const [createInternalDocumentWithVat, {isLoading: isCreateInternalDocumentWithVatLoading}] =
    useCreateInternalInvoiceDocumentWithVatDocumentMutation();
  const [
    createInternalCorrectiveTaxDocument,
    {isLoading: isCreateInternalCorrectiveTaxDocumentLoading},
  ] = useCreateInternalCorrectiveTaxDocumentDocumentMutation();
  const [createBrokerageInternalDocument, {isLoading: isCreateBrokerageInternalDocumentLoading}] =
    useCreateBrokerageInternalInvoiceDocumentDocumentMutation();
  const [
    createBrokerageInternalDocumentWithVat,
    {isLoading: isCreateBrokerageInternalDocumentWithVatLoading},
  ] = useCreateBrokerageInternalInvoiceDocumentWithVatDocumentMutation();

  const isLoading =
    isCreateInternalDocumentLoading ||
    isCreateInternalDocumentWithVatLoading ||
    isCreateBrokerageInternalDocumentLoading ||
    isCreateBrokerageInternalDocumentWithVatLoading ||
    isCreateInternalCorrectiveTaxDocumentLoading ||
    areTemplatesLoading;

  const isGenerateInternalInvoiceDocumentDisabled = isBrokerageInternalDocument
    ? !hasAclDocsWithPermission(canReadBrokerageInternalInvoiceDocument)
    : !hasAclDocsWithPermission(canReadInternalInvoiceDocument);

  const isGenerateInternalInvoiceDocumentDeductibleDisabled = isBrokerageInternalDocument
    ? !hasAclDocsWithPermission(canReadBrokerageInternalInvoiceDocumentWithVat)
    : !hasAclDocsWithPermission(canReadInternalInvoiceDocumentWithVat);

  const isGenerateInternalCorrectiveTaxDocumentDisabled = !hasAclDocsWithPermission(
    canReadInternalCorrectiveTaxDocument
  );

  const generateInternalInvoiceDocument = () => {
    const brokeragePrimaryTemplate = getTemplatesByCode(
      'brokerage-internal-invoice-document'
    ).primaryTemplate;
    const internalPrimaryTemplate = getTemplatesByCode('internal-invoice-document').primaryTemplate;

    const template = isBrokerageInternalDocument
      ? brokeragePrimaryTemplate
      : internalPrimaryTemplate;

    if (isNilOrEmpty(template?.id)) {
      showNotification.error('general.notifications.noTemplateFound');
      return;
    }

    return (isBrokerageInternalDocument ? createBrokerageInternalDocument : createInternalDocument)(
      {
        businessCaseId: businessCase.id,
        orderId: order.id,
        paymentId,
        createInternalInvoiceDocumentRequestBody: {
          templateId: template!.id,
        },
      }
    )
      .unwrap()
      .catch(handleApiError);
  };

  const generateInternalInvoiceDocumentDeductible = () => {
    const brokeragePrimaryTemplate = getTemplatesByCode(
      'brokerage-internal-invoice-document-with-vat'
    ).primaryTemplate;
    const internalPrimaryTemplate = getTemplatesByCode(
      'internal-invoice-document-with-vat'
    ).primaryTemplate;

    const template = isBrokerageInternalDocument
      ? brokeragePrimaryTemplate
      : internalPrimaryTemplate;

    if (isNilOrEmpty(template?.id)) {
      showNotification.error('general.notifications.noTemplateFound');

      return;
    }

    return (
      isBrokerageInternalDocument
        ? createBrokerageInternalDocumentWithVat
        : createInternalDocumentWithVat
    )({
      businessCaseId: businessCase.id,
      orderId: order.id,
      paymentId,
      createInternalInvoiceDocumentRequestBody: {
        templateId: template!.id,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const generateInternalCorrectiveTaxDocument = () => {
    const template = getTemplatesByCode('internal-corrective-tax-document').primaryTemplate;

    if (isNilOrEmpty(template?.id)) {
      showNotification.error('general.notifications.noTemplateFound');
      return;
    }

    return createInternalCorrectiveTaxDocument({
      internalCorrectiveTaxDocumentRequestBody: {
        orderId: order.id,
        paymentId,
        templateId: template!.id,
        checkoutId,
        taxDocumentId,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  return {
    generateInternalInvoiceDocumentDeductible,
    generateInternalInvoiceDocument,
    generateInternalCorrectiveTaxDocument,
    isLoading,
    isGenerateInternalInvoiceDocumentDisabled,
    isGenerateInternalInvoiceDocumentDeductibleDisabled,
    isGenerateInternalCorrectiveTaxDocumentDisabled,
  };
};
